<script setup lang="ts">
import { ref } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { Card } from '@/Components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs'
import { ArrowLeft } from 'lucide-vue-next'

// Import tutor components
import TutorHero from '@/Components/Tutors/TutorHero.vue'
import IntroVideo from '@/Components/Tutors/IntroVideo.vue'
import ContactTutor from '@/Components/Tutors/ContactTutor.vue'
import ShareProfile from '@/Components/Tutors/ShareProfile.vue'
import TrustBadges from '@/Components/Tutors/TrustBadges.vue'
import MembershipPlansCard from '@/Components/Tutors/MembershipPlansCard.vue'
import OverviewTab from '@/Components/Tutors/OverviewTab.vue'
import PricingPlans from '@/Components/Tutors/PricingPlans.vue'
import ContentLibrary from '@/Components/Tutors/ContentLibrary.vue'
import ReviewsTab from '@/Components/Tutors/ReviewsTab.vue'

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Tutor {
  id: number
  slug: string
  first_name: string
  last_name: string
  name: string
  email: string
  phone: string
  avatar: string
  highlight: string
  rating: number
  reviews_count: number
  students_count: number
  videos_count: number
  subjects: Array<{ id: number; name: string; slug: string; color: string; icon: string }>
  levels: Array<{ id: number; name: string }>
  examboards: Array<{ id: number; name: string }>
  about_me?: string
  academic_qualifications: any[]
  certifications: any[]
  services_offered: string[]
  price_base_tier: number
  price_silver_tier: number
  price_gold_tier: number
  group_session_availability: any[]
  one_on_one_availability: any[]
  teaching_experience?: string
  teaching_style?: string
  cv_path?: string
  intro_video?: string | null
  group_subjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
}

interface TimeSlot {
  day: string
  time: string
  available: boolean
  booked?: boolean
  locked?: boolean
}

interface LegendItem {
  label: string
  bg: string
}

interface Props {
  tutor: Tutor
  availability: TimeSlot[][]
  availabilityLegend?: LegendItem[]
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  availabilityLegend: () => [],
  subscriptions: () => [],
})

const activeTab = ref('overview')

const isSubscribedToTutor = props.subscriptions.some(
  (sub) => sub.teacher_id === props.tutor.id
)

const currentSubscription = props.subscriptions.find(
  (sub) => sub.teacher_id === props.tutor.id
)
</script>

<template>
  <Head :title="tutor.name" />

  <DashboardLayout>
    <div class="w-full max-w-7xl">
      <!-- Back Button -->
      <Link
        href="/student/explore"
        class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
      >
        <ArrowLeft class="w-4 h-4" />
        Back to Explore
      </Link>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Hero Section -->
          <TutorHero :tutor="tutor" />

          <!-- Already Subscribed Banner -->
          <div
            v-if="isSubscribedToTutor"
            class="bg-green-50 border border-green-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-2"
          >
            <div>
              <p class="text-green-800">
                  You're subscribed to
                  <strong>{{ tutor.first_name || tutor.name }}</strong>
                  on the
                  <strong>{{ currentSubscription?.tier }}</strong>
                  plan
              </p>
            </div>
            <Link
              href="/student/subscriptions"
              class="text-green-700 hover:text-green-900 text-sm font-medium"
            >
              Manage
            </Link>
          </div>

          <!-- Introduction Video -->
          <IntroVideo
            v-if="tutor.intro_video"
            :tutor-name="tutor.first_name"
            :video-title="`Meet ${tutor.first_name} - Your Tutor`"
            :playback-id="tutor.intro_video"
          />

          <!-- Tabs Section -->
          <Card class="border-0 shadow-sm">
            <Tabs v-model="activeTab" class="w-full">
              <TabsList class="w-full justify-start border-0 border-b border-gray-200 rounded-none bg-transparent p-0 px-6 h-auto overflow-x-auto">
                <TabsTrigger
                  value="overview"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="membership"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                    <span class="md:hidden">Plans</span>
                    <span class="hidden md:block">Membership Plans</span>
                </TabsTrigger>
                <TabsTrigger
                  value="content"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                    <span class="md:hidden">Content</span>
                    <span class="hidden md:block">Content Library</span>
                </TabsTrigger>
                <TabsTrigger
                  value="reviews"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                  Reviews
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" class="p-6">
                <OverviewTab :tutor="tutor" :availability="availability" :legend="availabilityLegend" :subscriptions="subscriptions" />
              </TabsContent>

              <TabsContent value="membership" class="p-6">
                <PricingPlans
                  :tutor-slug="tutor.slug"
                  :tutor-id="tutor.id"
                  :base-price="tutor.price_base_tier"
                  :silver-price="tutor.price_silver_tier"
                  :gold-price="tutor.price_gold_tier"
                  :services-offered="tutor.services_offered"
                  :videos-count="tutor.videos_count"
                  :subjects="tutor.subjects"
                  :group-subjects="tutor.group_subjects"
                  :subscriptions="subscriptions"
                />
              </TabsContent>

              <TabsContent value="content" class="p-6">
                <ContentLibrary :tutor-slug="tutor.slug" />
              </TabsContent>

              <TabsContent value="reviews" class="p-6">
                <ReviewsTab />
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1">
          <div class="space-y-6">
            <!-- Membership Plans Card -->
            <MembershipPlansCard
              v-if="!isSubscribedToTutor"
              :tutor-slug="tutor.slug"
              :tutor-id="tutor.id"
              :tutor-name="tutor.first_name"
              :base-price="tutor.price_base_tier"
              :silver-price="tutor.price_silver_tier"
              :gold-price="tutor.price_gold_tier"
              :videos-count="tutor.videos_count"
              :services-offered="tutor.services_offered"
              :group-subjects="tutor.group_subjects"
              :subscriptions="subscriptions"
            />

            <!-- Contact Tutor -->
            <ContactTutor :tutor-name="tutor.first_name" />

            <!-- Share Profile -->
            <ShareProfile />

            <!-- Trust Badges -->
            <TrustBadges />
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>
